import { moveItemInArray } from "@angular/cdk/drag-drop";
import { on } from "@ngrx/store";

import { cloneDeep } from "lodash";

import { BaseChargeFragment, CalculateDistanceOutput, CalendarEvent_LocationFragment, JobDetailsV2Fragment, Job_CalendarEventFragment, Job_LocationFragment } from "../../../../../../generated/graphql.generated";
import { EventLocationTypes, EventTypeInfo, eventTypeInfoMapV2, locationNameMap } from '../../../../../global.constants';


import { HOURS_TO_SECONDS } from "../../../../../time";

import { generateUUID } from "../../../../../utilities/state.util";
import { calculateChargesRelatedToEvent, getDistanceBetweenLocationTypes } from "../../../../job-events-helpers";
import { BookEventDialogActions } from "../timeline-book-event.actions";

import { EventItineraryActions } from "./event-itinerary.actions";
import { EditableLocation, EventItineraryState, eventItineraryInitialState } from "./event-itinerary.state";

interface EventLocation {
  id: string;
  order: number;
  locationType: EventLocationTypes;
  location: CalendarEvent_LocationFragment;
  estimatedTimeAtLocation: number;
  travelTimeToNextLocation: number;
  travelTimeToNextLocationOffset: number;
  distanceToNextLocation: number;
}

export const saveEventChangesTriggers = [
  EventItineraryActions.countdownCompleted,
  EventItineraryActions.calculateNowButtonClicked,
  BookEventDialogActions.saveAsButtonClicked,
];

export const eventItineraryReducers = [
  on(
    BookEventDialogActions.dialogOpened,
    (state: EventItineraryState, { event, job, zoneEstimateLength, zoneVoseLength, zoneIncludeDockTravel }): EventItineraryState => {

      const eventItinerary = { ...cloneDeep(state.eventItinerary) };

      const isScheduled = ![ 'required', 'pending' ].includes(event.status);

      const dockStart = (event.locations || []).find((l) => l.type === EventLocationTypes.dockStart);

      if (zoneIncludeDockTravel || (dockStart && isScheduled)) {
        eventItinerary.includeDockTravel = true;
      }

      const eventTypeDetails = eventTypeInfoMapV2[event.type];

      const locations = getEditableLocations({
        event,
        job,
        includeDockTravel: eventItinerary.includeDockTravel,
        timeConfigs: {
          estimating: zoneEstimateLength,
          virtualEstimate: zoneVoseLength,
        },
        skipSetDefaults: Boolean(eventTypeDetails?.forceDefaultLocationTimes),
      });

      if (eventTypeDetails?.forceDefaultLocationTimes) {
        applyForcedDefaultLocationTimes(locations, event.type);
      }

      eventItinerary.locations = locations;

      return {
        ...state,
        eventItinerary,
      }
    },
  ),
  on(
    EventItineraryActions.toggleExpandLocationButtonClicked,
    (state: EventItineraryState, { locationId, collapsed }): EventItineraryState => {
    return {
      ...state,
      eventItinerary: {
        ...state.eventItinerary,
        locations: {
          ...state.eventItinerary.locations,
          [locationId]: {
            ...state.eventItinerary.locations[locationId],
            collapsed,
          },
        },
      }
  }
  }),
  on(EventItineraryActions.locationDropped, (state: EventItineraryState, { previousIndex, currentIndex }): EventItineraryState => {

    const locations = cloneDeep(state.eventItinerary.locations);

    const sortedLocations = Object.values(state.eventItinerary.locations)
      .sort((a, b) => a.order - b.order);

    moveItemInArray(sortedLocations, previousIndex, currentIndex);

    for (const [ index, location ] of sortedLocations.entries()) {
      locations[location.id] = {
        ...location,
        order: index,
      }
    }

    return {
      ...state,
      eventItinerary: {
        ...state.eventItinerary,
        locations,
      }
  }
  }),
  on(
    EventItineraryActions.estimatedTimeAtLocationInputChanged,
    (state: EventItineraryState, { locationId, duration }): EventItineraryState => {
    return {
      ...state,
      eventItinerary: {
        ...state.eventItinerary,
        locations: {
          ...state.eventItinerary.locations,
          [locationId]: {
            ...state.eventItinerary.locations[locationId],
            estimatedTimeAtLocation: duration,
          },
        },
      }
  }
  }),
  on(
    EventItineraryActions.travelTimeToNextLocationInputChanged,
    (state: EventItineraryState, { locationId, duration }): EventItineraryState => {
    return {
      ...state,
      eventItinerary: {
        ...state.eventItinerary,
        locations: {
          ...state.eventItinerary.locations,
          [locationId]: {
            ...state.eventItinerary.locations[locationId],
            totalTravelTimeToNextLocation: duration,
          },
        },
      }
    }
  }),
  on(EventItineraryActions.addStopButtonClicked, (state: EventItineraryState): EventItineraryState => {

    const newLocationId = generateUUID();

    const locations = cloneDeep(state.eventItinerary.locations);

    normalizeLocationOrder(locations);

    locations[newLocationId] = {
      id: newLocationId,
      distanceToNextLocation: 0,
      totalTravelTimeToNextLocation: 0,
      estimatedTimeAtLocation: 0,
      order: Object.keys(state.eventItinerary.locations).length,
      collapsed: false,
    }

    return {
      ...state,
      eventItinerary: {
        ...state.eventItinerary,
        locations,
      }
  }
  }),
  on(EventItineraryActions.dropdownLocationSelected, (state: EventItineraryState, { locationId, location }): EventItineraryState => {
    return {
      ...state,
      eventItinerary: {
        ...state.eventItinerary,
        locations: {
          ...state.eventItinerary.locations,
          [locationId]: {
            ...state.eventItinerary.locations[locationId],
            locationType: location.type as EventLocationTypes,
            location: location.originalLocation,
          }
        }
      }
    }
  }),
  on(
    EventItineraryActions.autocompleteLocationSelected,
    EventItineraryActions.addLocationToJobSuccess,
    (state: EventItineraryState, { locationId, location }): EventItineraryState => {
      return {
        ...state,
        eventItinerary: {
          ...state.eventItinerary,
          locations: {
            ...state.eventItinerary.locations,
            [locationId]: {
              ...state.eventItinerary.locations[locationId],
              location,
            }
          }
        }
      }
    },
  ),
  on(EventItineraryActions.deleteLocationButtonClicked, (state: EventItineraryState, { locationId }): EventItineraryState => {

    const locations = cloneDeep(state.eventItinerary.locations);

    delete locations[locationId];

    return {
      ...state,
      eventItinerary: {
        ...state.eventItinerary,
        locations,
      }
    }
  }),
  on(
    EventItineraryActions.includeDockTravelTimeCheckboxToggled,
    (state: EventItineraryState, { includeDockTravel }): EventItineraryState => ({
    ...state,
    eventItinerary: {
      ...state.eventItinerary,
      includeDockTravel,
    }
  })),
  on(EventItineraryActions.locationDataReloaded, (state: EventItineraryState, { job, event }): EventItineraryState => {

    const eventItinerary = { ...cloneDeep(state.eventItinerary) };

    const locations = getUpdatedEditableLocations({
      job,
      event,
      locations: state.eventItinerary.locations,
      includeDockTravel: state.eventItinerary.includeDockTravel,
    });

    const eventTypeDetails = eventTypeInfoMapV2[event.type];
    if (eventTypeDetails?.forceDefaultLocationTimes) {
      applyForcedDefaultLocationTimes(locations, event.type);
    }

    eventItinerary.locations = locations;

    return {
      ...state,
      eventItinerary,
    }
  }),
  on(EventItineraryActions.countdownStarted, (state: EventItineraryState, { initialCountdown }): EventItineraryState => {
    return {
      ...state,
      eventItinerary: {
        ...state.eventItinerary,
        saveEventCountdown: initialCountdown,
        saveEventCountdownActive: true,
      },
    }
  }),
  on(EventItineraryActions.countdownStopped, (state: EventItineraryState): EventItineraryState => {
    return {
      ...state,
      eventItinerary: {
        ...state.eventItinerary,
        saveEventCountdownActive: false,
      },
    }
  }),
  on(EventItineraryActions.countdownTicked, (state: EventItineraryState): EventItineraryState => {

    const eventItinerary = { ...state.eventItinerary };

    eventItinerary.saveEventCountdown--;

    return {
      ...state,
      eventItinerary,
    }
  }),
  on(BookEventDialogActions.closeBookDialogButtonClicked, (state: EventItineraryState): EventItineraryState => ({
    ...state,
    eventItinerary: {
      ...eventItineraryInitialState.eventItinerary,
    }
  })),
  on(
    EventItineraryActions.saveEventChangesSuccess,
    (state: EventItineraryState, { updatedEvent, job, promptedBy }): EventItineraryState => {

    if (promptedBy.type === BookEventDialogActions.saveAsButtonClicked.type) {
      return {
        ...state,
        eventItinerary: {
          ...eventItineraryInitialState.eventItinerary,
        }
      }
    }

    const eventItinerary = { ...cloneDeep(state.eventItinerary) };

    const eventTypeDetails = eventTypeInfoMapV2[updatedEvent.type];

    const locations = getEditableLocations({
      job,
      event: updatedEvent,
      includeDockTravel: eventItinerary.includeDockTravel,
      timeConfigs: {
        virtualEstimate: undefined,
        estimating: undefined,
      },
      skipSetDefaults: true,
    });

    if (eventTypeDetails?.forceDefaultLocationTimes) {
      applyForcedDefaultLocationTimes(locations, updatedEvent.type);
    }

    eventItinerary.locations = locations;

    return {
      ...state,
      eventItinerary,
    }
  }),
];

function getEditableLocations(input: {
  job: JobDetailsV2Fragment;
  event: Job_CalendarEventFragment;
  includeDockTravel: boolean;
  timeConfigs: {
    estimating: number | undefined;
    virtualEstimate: number | undefined;
  },
  skipSetDefaults?: boolean;
}): { [ locationId: string ]: EditableLocation }{

  const eventLocations = buildEventLocations(input);

  const {
    event,
    timeConfigs,
    skipSetDefaults,
  } = input;

  const eventTypeDetails = eventTypeInfoMapV2[event.type];

  // Set default times
  if (!skipSetDefaults && !event.start) {

    const charges = event.charges || [];

    const timeAtLocationAdjusted = adjustEstimatedTimeAtLocationBasedOnCharges({
      eventLocations,
      charges,
      virtualTime: eventTypeDetails?.virtualTime,
    })

    if (!timeAtLocationAdjusted) {
      setDefaultEstimatedTimeAtLocation(eventLocations, eventTypeDetails, timeConfigs);
    }

    adjustTravelTimeToNextLocationOffsetBasedOnCharges({
      eventLocations,
      charges,
    });
  }

  const output: { [ locationId: string ]: EditableLocation } = {};

  for (const eventLocation of eventLocations) {

    const totalTravelTimeToNextLocation = eventLocation.travelTimeToNextLocation + eventLocation.travelTimeToNextLocationOffset;

    output[eventLocation.id] = {
      id: eventLocation.id,
      locationType: eventLocation.locationType,
      estimatedTimeAtLocation: eventLocation.estimatedTimeAtLocation,
      totalTravelTimeToNextLocation,
      distanceToNextLocation: eventLocation.distanceToNextLocation,
      collapsed: true,
      order: eventLocation.order,
      location: eventLocation.location.location,
    }
  }

  if (!input.includeDockTravel) {
    return output;
  }

  const editableLocations = Object.values(output);

  if (editableLocations.length === 0) { return output; }

  const hasDockStart = editableLocations.some((l) => l.locationType === EventLocationTypes.dockStart);
  const hasDockEnd = editableLocations.some((l) => l.locationType === EventLocationTypes.dockEnd);

  if (hasDockStart && hasDockEnd) {
    return output;
  }

  return getUpdatedEditableLocations({
    job: input.job,
    event: input.event,
    locations: output,
    includeDockTravel: input.includeDockTravel,
  });
}

function applyForcedDefaultLocationTimes(
  locations: { [locationId: string]: EditableLocation },
  eventType: string,
): void {
  const eventTypeDetails = eventTypeInfoMapV2[eventType];
  for (const location of Object.values(locations)) {
    location.estimatedTimeAtLocation =
      eventTypeDetails?.defaultLocationTimes?.[location.locationType ?? ''] ?? 0;
    location.totalTravelTimeToNextLocation = 0;
    location.distanceToNextLocation = 0;
  }
}

function buildEventLocations(input: {
  job: JobDetailsV2Fragment;
  event: Job_CalendarEventFragment;
  includeDockTravel: boolean;
}): EventLocation[] {

  const {
    job,
    event,
    includeDockTravel,
  } = input;

  if (event?.locations?.length) {
    return event.locations.map((location) => ({
      id: location.id,
      order: location.order,
      locationType: location.type as EventLocationTypes,
      location,
      estimatedTimeAtLocation: location?.estimatedTimeAtLocation || 0,
      travelTimeToNextLocation: location?.travelTimeToNextLocation || 0,
      travelTimeToNextLocationOffset: location?.travelTimeToNextLocationOffset || 0,
      distanceToNextLocation: 0,
    }));
  }

  // If event has no locations, build locations from job
  const eventLocations: EventLocation[] = [];

  const eventLocationTypes = buildEventLocationTypes({
    event,
    includeDockTravel,
  });

  for (const [ index, eventLocationType ] of eventLocationTypes.entries()) {

    const jobLocationType = locationNameMap[eventLocationType];

    const jobLocation = job.locations.find((l) => l.locationType === jobLocationType);

    if (!jobLocation) { continue; }

    const calendarEventLocation: CalendarEvent_LocationFragment = {
      id: jobLocation.locationId,
      type: eventLocationType,
      location: jobLocation.location,
      order: index,
    }

    const eventLocation: EventLocation = {
      ...calendarEventLocation,
      locationType: eventLocationType,
      location: calendarEventLocation,
      estimatedTimeAtLocation: 0,
      travelTimeToNextLocation: 0,
      travelTimeToNextLocationOffset: 0,
      distanceToNextLocation: 0,
    }

    eventLocations.push(eventLocation);

    const nextLocationType = eventLocationTypes[index + 1];

    if (!nextLocationType) { continue; }

    const distanceDetails: CalculateDistanceOutput = getDistanceBetweenLocationTypes(
      eventLocationType,
      nextLocationType,
      job.distances || {},
    );

    if (!distanceDetails) { continue; }

    eventLocation.travelTimeToNextLocation = distanceDetails.estimatedTime;
    eventLocation.distanceToNextLocation = distanceDetails.totalDistance;
  }

  return eventLocations;
}

export function buildEventLocationTypes(input: {
  event: Job_CalendarEventFragment;
  includeDockTravel: boolean;
}): EventLocationTypes[] {

  const eventLocationTypes: EventLocationTypes[] = [];

  const {
    event,
    includeDockTravel,
  } = input;

  const eventTypeDetails = eventTypeInfoMapV2[event.type];

  if ('virtualTime' in eventTypeDetails) { return eventLocationTypes; }

  if (includeDockTravel) {
    eventLocationTypes.push(EventLocationTypes.dockStart);
  }

  eventLocationTypes.push(EventLocationTypes.start);

  if (event.type === 'moving') {
    eventLocationTypes.push(EventLocationTypes.end);
  }

  if (includeDockTravel) {
    eventLocationTypes.push(EventLocationTypes.dockEnd);
  }

  return eventLocationTypes;
}

function findLocationByEventLocationType(input: {
  job: JobDetailsV2Fragment;
  event: Job_CalendarEventFragment;
  eventLocationType: EventLocationTypes;
}): Omit<CalendarEvent_LocationFragment, 'order'> {

  const { job, event, eventLocationType } = input;

  const eventLocation = event.locations?.find((l) => l.type === eventLocationType);

  if (eventLocation) { return eventLocation };

  const jobLocationType = locationNameMap[eventLocationType];

  const jobLocation = job.locations.find((l) => l.locationType === jobLocationType);

  if (!jobLocation) { return; }

  return castJobLocationIntoEventLocation(jobLocation, eventLocationType);
}

export function castJobLocationIntoEventLocation(
  location: Job_LocationFragment,
  eventLocationType: EventLocationTypes,
): Omit<CalendarEvent_LocationFragment, 'order'> {
  return {
    id: location.locationId,
    type: eventLocationType,
    location: location.location,
  }
}

function adjustEstimatedTimeAtLocationBasedOnCharges({
  charges,
  virtualTime,
  eventLocations,
}: {
  eventLocations: EventLocation[];
  charges: BaseChargeFragment[];
  virtualTime?: number;
}): boolean {

  let totalLocationTime = eventLocations.reduce((curr, l) => curr + l.estimatedTimeAtLocation, 0);

  if (virtualTime) {
    totalLocationTime += virtualTime;
  }

  const chargeTotals = calculateChargesRelatedToEvent(charges, 'locationtime');

  const doAdjust = chargeTotals.quantitySum > 0 || chargeTotals.charges.length > 0;

  if (!doAdjust) {
    return false;
  }

  const { timeMultiplier, additionalTime } = getAdditionalTime(chargeTotals, totalLocationTime);

  const nonBaseLocations = eventLocations.filter((l) => !isBaseLocation(l));

  for (const location of nonBaseLocations) {
    const adjustedTime = Math.ceil(location.estimatedTimeAtLocation * timeMultiplier + additionalTime) / nonBaseLocations.length;
    location.estimatedTimeAtLocation = adjustedTime;
  }

  return true;
}

function adjustTravelTimeToNextLocationOffsetBasedOnCharges({
  eventLocations,
  charges,
}: {
  eventLocations: EventLocation[];
  charges: BaseChargeFragment[];
}){

  const totalTravelTime = eventLocations.reduce((curr, l) => curr + l.travelTimeToNextLocation, 0);

  const chargeTotals = calculateChargesRelatedToEvent(charges, 'traveltime');

  const { timeMultiplier, additionalTime } = getAdditionalTime(chargeTotals, totalTravelTime);

  for (const location of eventLocations) {
    const adjustedTravelTime = Math.ceil(
      location.travelTimeToNextLocation * timeMultiplier + additionalTime / eventLocations.length
    );
    location.travelTimeToNextLocationOffset = adjustedTravelTime - location.travelTimeToNextLocation;
  }
}

function getAdditionalTime(
  chargeTotals: ReturnType<typeof calculateChargesRelatedToEvent>,
  eventTimeTotal: number,
): {
  timeMultiplier: number;
  additionalTime: number;
} {

  if (chargeTotals.charges.length === 0) {
    return {
      timeMultiplier: 1,
      additionalTime: 0,
    }
  }

  if (eventTimeTotal === 0 && chargeTotals.quantitySum === 0) {
    return {
      timeMultiplier: 1,
      additionalTime: 0,
    }
  }

  const chargeTime = chargeTotals.quantitySum * HOURS_TO_SECONDS;

  if (eventTimeTotal === 0 && chargeTotals.quantitySum > 0) {
    return {
      timeMultiplier: 1,
      additionalTime: chargeTime,
    }
  }

  return {
    timeMultiplier: chargeTime / eventTimeTotal,
    additionalTime: 0,
  }
}

function setDefaultEstimatedTimeAtLocation(
  eventLocations: EventLocation[],
  eventTypeInfo: EventTypeInfo,
  timeConfigs: {
    estimating: number | undefined;
    virtualEstimate: number | undefined;
  },
) {

  for (const eventLocation of eventLocations) {

    const zoneDefaultEventLength = timeConfigs[eventTypeInfo.value];

    if (eventLocation.locationType === EventLocationTypes.start && zoneDefaultEventLength) {
      eventLocation.estimatedTimeAtLocation = zoneDefaultEventLength;
      continue;
    }

    const eventTypeDefaultLength = eventTypeInfo.defaultLocationTimes?.[eventLocation.locationType];

    if (eventTypeDefaultLength) {
      eventLocation.estimatedTimeAtLocation = eventTypeDefaultLength;
      continue;
    }
  }
}

function isBaseLocation(eventLocation: EventLocation): boolean {
  const { locationType } = eventLocation;
  return locationType === EventLocationTypes.dockStart || locationType === EventLocationTypes.dockEnd;
}

function getUpdatedEditableLocations(input: {
  job: JobDetailsV2Fragment;
  event: Job_CalendarEventFragment;
  locations: typeof eventItineraryInitialState['eventItinerary']['locations'];
  includeDockTravel: boolean;
}): { [ locationId: string ]: EditableLocation } {

  const {
    job,
    event,
    includeDockTravel,
  } = input;

  const locations = cloneDeep(input.locations);

  const orderedLocations = Object.values(locations).sort((a, b) => a.order - b.order);

  if (!includeDockTravel) {

    const dockStart = orderedLocations.find((l) => l.locationType === EventLocationTypes.dockStart);

    if (dockStart) {
      delete locations[dockStart.id];
    }

    const dockEnd = orderedLocations.find((l) => l.locationType === EventLocationTypes.dockEnd);

    if (dockEnd) {

      delete locations[dockEnd.id];

      const secondLastStop = orderedLocations[orderedLocations.length - 2];
      secondLastStop.distanceToNextLocation = 0;
      secondLastStop.totalTravelTimeToNextLocation = 0;
    }

    return locations;
  }

  const hasDockStart = orderedLocations.some((l) => l.locationType === EventLocationTypes.dockStart);

  if (!hasDockStart) {
    const dockStart = findLocationByEventLocationType({
      event,
      job,
      eventLocationType: EventLocationTypes.dockStart,
    });

    const firstStop = orderedLocations[0];

    if (dockStart && firstStop) {
      const dockStartTotalTravelTime = (dockStart.travelTimeToNextLocation || 0) + (dockStart.travelTimeToNextLocationOffset || 0);

      const dockStartId = generateUUID();

      locations[dockStartId] = {
        id: dockStartId,
        order: firstStop.order - 1,
        locationType: EventLocationTypes.dockStart,
        estimatedTimeAtLocation: dockStart.estimatedTimeAtLocation || 0,
        collapsed: true,
        location: dockStart.location,
        totalTravelTimeToNextLocation: dockStartTotalTravelTime,
        distanceToNextLocation: dockStart.distanceToNextLocation || 0,
      }

      const dockStartToFirstStopCDO: CalculateDistanceOutput = getDistanceBetweenLocationTypes(
        EventLocationTypes.dockStart,
        firstStop.locationType,
        job?.distances || {},
      );

      if (dockStartToFirstStopCDO) {
        locations[dockStartId].distanceToNextLocation = dockStartToFirstStopCDO.totalDistance;
        locations[dockStartId].totalTravelTimeToNextLocation = dockStartToFirstStopCDO.estimatedTime +
          (dockStart.travelTimeToNextLocationOffset || 0);
      }
    }
  }


  const hasDockEnd = orderedLocations.some((l) => l.locationType === EventLocationTypes.dockEnd);

  if (!hasDockEnd) {
    const dockEnd = findLocationByEventLocationType({
      event,
      job,
      eventLocationType: EventLocationTypes.dockEnd,
    });

    const lastStop = orderedLocations[orderedLocations.length - 1];

    if (dockEnd && lastStop) {

      const dockEndId = generateUUID();

      locations[dockEndId] = {
        id: dockEndId,
        order: lastStop.order + 1,
        locationType: EventLocationTypes.dockEnd,
        estimatedTimeAtLocation: dockEnd.estimatedTimeAtLocation || 0,
        collapsed: true,
        location: dockEnd.location,
        totalTravelTimeToNextLocation: 0,
        distanceToNextLocation: 0,
      }

      const lastStopToDockEndCDO: CalculateDistanceOutput = getDistanceBetweenLocationTypes(
        lastStop.locationType,
        EventLocationTypes.dockEnd,
        job?.distances || {},
      );

      if (lastStopToDockEndCDO) {
        lastStop.distanceToNextLocation = lastStopToDockEndCDO.totalDistance;
        lastStop.totalTravelTimeToNextLocation = lastStopToDockEndCDO.estimatedTime +
          (dockEnd.travelTimeToNextLocationOffset || 0);
      }
    }
  }

  normalizeLocationOrder(locations);

  return locations;
}

function normalizeLocationOrder(locations: { [ locationId: string ]: EditableLocation }) {
  const sortedLocations = Object.values(locations).sort((a, b) => a.order - b.order);

  for (const [ index, location ] of sortedLocations.entries()) {
    locations[location.id].order = index;
  }

  return locations;
}
